import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { translate } from '@ngneat/transloco';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, delay, expand, filter, finalize, reduce, take, takeUntil } from 'rxjs/operators';
import { GSTFilingState } from 'src/app/modules/taxilla-library/lib/store/states';
import { AlertSuccess } from '../../store/actions/shared.actions';
import { ApiService } from '../../services/api/api.service';
import { AssetData } from '../../models/assetdata.class';
import { AssetService } from '../../models/assetservice.class';
import {
    ClearRecordsPagination,
    ClearSearchCriteria,
    DeletedRecords,
    DeleteRecords,
    SaveRecords,
    SetRecords,
} from '../../store/actions/records.actions';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { Entity } from '../../models/entity.class';
import { Field } from '../../models/field.class';
import { getApp$ } from '../../store/selectors/apps.selector';
import {
    getClearanceRunNumber$,
    getFilingEntityRecords$,
    getFilingEntityRecordsCount$,
    getFilingGroupedRecords$,
    getFilingGroupedRecordsCount$,
    getFilingGroupedRecordsPagination$,
    getFilingInstanceRecordsStatus$,
    getFilingNonSelectedEntity$,
    getFilingParentEntityDetails$,
    getFilingPrimaryEntityRecords$,
    getFilingRecordsPagination$,
    getFilingSelectedAppMetaData$,
    getFilingSelectedEntity$,
    getFilingSelectedEntityDetails$,
    getFilingSource1Records$,
    getFilingSource1RecordsCount$,
    getFilingSource1RecordsPagination$,
    getFilingSource2Records$,
    getFilingSource2RecordsCount$,
    getFilingSource2RecordsPagination$,
    getFilingState$,
} from '../../store/selectors/gst-filing.selector';
import { getCurrentOrganizationId$ } from '../../store/selectors/session.selector';
import {
    GetFilingInstanceEntityRecords,
    SetFilingInstanceEntityCriteria,
    SetFilingInstanceEntityPagination,
    SetFilingInstanceEntityView,
    SetFilingSelectedInstanceDetails,
} from '../../store/actions/gst-filing.actions';
import { Record } from '../../models/record/record.class';
import { RecordPayload } from '../../models/payload/recordpayload.class';
import { SetInstanceDataTableRecordSelect } from '../../store/actions/instances.actions';
import { TableColumn } from '../../interface/table-column.interface';
import { TableData } from '../../interface/table-data.interface';
import { UserPreferencesService } from '../../services/user-preferences/user-preferences.service';
import { UtilsService } from '../../services/utils/utils.service';

import { ColumnMatchingDialogComponent, ColumnMatchingDialogData } from '../column-matching-dialog/column-matching-dialog.component';
import {
    ToleranceDetailsDialogComponent,
    ToleranceDetailsDialogData,
} from '../tolerance-details-dialog/tolerance-details-dialog.component';

@Component({
    selector: 'app-gst-instance-data-dialog',
    templateUrl: './instance-data-dialog.component.html',
    styleUrls: ['./instance-data-dialog.component.scss'],
    standalone: false,
})
export class InstanceDataDialogComponent implements OnInit {
    public source1tableData: BehaviorSubject<TableData[]> = new BehaviorSubject([]);
    public source2tableData: BehaviorSubject<TableData[]> = new BehaviorSubject([]);
    public source1: string;
    public source2: string;
    public source1_allColumns: BehaviorSubject<TableColumn[]> = new BehaviorSubject([]);
    public source2_allColumns: BehaviorSubject<TableColumn[]> = new BehaviorSubject([]);
    public source1_selectedColumns: string[] = [];
    public source2_selectedColumns: string[] = [];
    private isApplyingMatchedColumns = false;
    public selectedEntity: Entity;
    private assetMetaData: AssetData;
    public entities: Entity[] = [];
    public serviceId: string;
    public instanceId: string;
    public name: string;
    public stateName: Observable<string>;
    private unSubscribe = new Subject();
    public isConfigurationLoaded = false;
    private currentView: { recordId: string; entityId: string; level: number };
    primaryEntity: any;
    public source1Pagination = {
        index: 0,
        size: 20,
        count: 0,
    };
    public source2Pagination = {
        index: 0,
        size: 20,
        count: 0,
    };
    public dataLength: number;
    public loadingStatus: Observable<boolean>;
    public clearanceRunNumber: number;
    public selectedRecordsCount = 0;
    public moveToGroup: string;
    private nonSelectedEntities: Entity[];
    public entityRecords: {
        source1: Record[];
        source2: Record[];
    } = {
        source1: [],
        source2: [],
    };
    private recordIdsToMove: string[];
    public demoMode = false;
    public dialogRef: any;
    public misMatchEntityId: string;
    private movableEntityIds: string[];
    private confirmMatchEntityId: string;
    public isMisMatchGroup: boolean;
    public canShowMovableEntities: boolean;
    public filteredNonSelectedEntities: Entity[];
    searchedColumnsData: { name: ''; value: '' }[] = [];
    private organizationId: string;
    private pageIndexVsRecords: {
        source1: {
            [index: number]: {
                tableData: TableData[];
                records: Record[];
            };
        };
        source2: {
            [index: number]: {
                tableData: TableData[];
                records: Record[];
            };
        };
    } = {
        source1: {},
        source2: {},
    };
    public isGrouped: boolean;
    public selectedDetails: GSTFilingState['selected']['viewLevel']['level'];
    public table1_totalCount = 0;
    public table2_totalCount = 0;
    public variance_totalCount = 0;
    public selectedTable1Id: string;
    public selectedTable2Id: string;
    public table1NumericFields: any[] = [];
    public table2NumericFields: any[] = [];
    public previousSelectedEntityUid: string;
    private primaryEntityRecord: Record;
    public isExporting: { source1: boolean; source2: boolean } = { source1: false, source2: false };
    public app: AssetService;
    private toleranceWarningOpen = false;

    private validateTolerances = (onProceed?: () => void): boolean => {
        if (this.toleranceWarningOpen) {
            return false;
        }
        const toleranceFields =
            this.primaryEntity?.fields?.filter(
                field =>
                    ['DOUBLE', 'DECIMAL', 'FLOAT', 'NUMBER', 'INT', 'LONG', 'INTEGER'].includes(field?.datatype) &&
                    (field?.name?.toLowerCase().includes('tolerance') ||
                        field?.displayName?.toLowerCase().includes('tolerance') ||
                        field?.uid?.toLowerCase().includes('tolerance'))
            ) || [];

        if (toleranceFields.length === 0) {
            return true;
        }

        const selectedRecords = this.getSelectedRecords();
        const total1 = selectedRecords.source1?.reduce((total, record) => {
            return total + CommonUtilsService.toNumber(record.fields.find(field => field.id === this.selectedTable1Id)?.value);
        }, 0);

        const total2 = selectedRecords.source2?.reduce((total, record) => {
            return total + CommonUtilsService.toNumber(record.fields.find(field => field.id === this.selectedTable2Id)?.value);
        }, 0);

        const variance = total1 - total2;
        const failingFields: ToleranceDetailsDialogData['failingFields'] = [];

        toleranceFields.forEach(field => {
            const recordField = this.primaryEntityRecord?.fields?.find(recordField => recordField.id === field.uid);
            const toleranceValue = CommonUtilsService.toNumber(recordField?.value);
            if (!isNaN(toleranceValue) && Math.abs(variance) > Math.abs(toleranceValue)) {
                failingFields.push({
                    name: field.displayName || field.name || field.uid,
                    tolerance: toleranceValue,
                    exceededBy: Math.abs(variance) - Math.abs(toleranceValue),
                });
            }
        });

        if (failingFields.length > 0) {
            this.toleranceWarningOpen = true;
            const dialogRef = this.dialog.open<ToleranceDetailsDialogComponent, ToleranceDetailsDialogData, boolean>(
                ToleranceDetailsDialogComponent,
                {
                    width: '780px',
                    maxWidth: '95vw',
                    maxHeight: '90vh',
                    panelClass: 'toleranceWarningDialog',
                    role: 'alertdialog',
                    autoFocus: '.warning-cancel',
                    data: {
                        sources: [
                            {
                                name: this.source1 || translate('Source 1'),
                                column:
                                    this.table1NumericFields.find(column => column.id === this.selectedTable1Id)?.name ||
                                    this.selectedTable1Id ||
                                    translate('Not selected'),
                                recordCount: selectedRecords.source1?.length || 0,
                                total: total1,
                            },
                            {
                                name: this.source2 || translate('Source 2'),
                                column:
                                    this.table2NumericFields.find(column => column.id === this.selectedTable2Id)?.name ||
                                    this.selectedTable2Id ||
                                    translate('Not selected'),
                                recordCount: selectedRecords.source2?.length || 0,
                                total: total2,
                            },
                        ],
                        variance,
                        failingFields,
                    } as ToleranceDetailsDialogData,
                }
            );
            dialogRef
                .afterClosed()
                .pipe(
                    take(1),
                    takeUntil(this.unSubscribe),
                    finalize(() => (this.toleranceWarningOpen = false))
                )
                .subscribe(confirmed => {
                    if (confirmed === true) {
                        onProceed?.();
                    }
                });
            return false;
        }
        return true;
    };

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialog: MatDialog,
        private store$: Store,
        private actions$: Actions,
        private _utils: UtilsService,
        public _commonUtils: CommonUtilsService,
        private userPreferences: UserPreferencesService,
        private _api: ApiService
    ) {}

    public closeModal = () => {
        this.demoMode ? this.dialogRef.close() : this.dialog.closeAll();
    };

    private addCheckboxColumn = () => {
        return {
            id: 'SELECTRECORD',
            hide: false,
            hideInAllColumnsList: true,
            icon: undefined,
            name: 'CheckBox',
            type: 'SELECTRECORD',
            options: undefined,
            showCheckboxInHeader: true,
        };
    };

    private loadTableData = (records: Record[], sourceType?: 'source1' | 'source2' | 'grouped') => {
        switch (sourceType) {
            case 'source2':
                this.entityRecords.source2 = records;
                break;
            default:
                this.entityRecords.source1 = records;
                break;
        }
        const source1Rows: TableData[] = [];
        const source2Rows: TableData[] = [];
        const source1AssociationIds = [];
        const source2AssociationIds = [];
        const mainAssociationIds = [];
        let source1Key = '',
            source2Key = '',
            primaryAssociationKey = '',
            secondaryAssociationKey = '',
            mainAssociationKey = '';

        this.selectedEntity?.fields
            .filter(field => field?.tagEntries?.find(entry => entry.value === 'invoice_number'))
            .forEach(field => {
                if (field.fieldSection === this.source1) {
                    source1Key = field.uid;
                } else if (field.fieldSection === this.source2) {
                    source2Key = field.uid;
                }
            });
        this.selectedEntity?.fields
            .filter(field => field?.tagEntries?.find(entry => entry.key === 'association'))
            .forEach(field => {
                const association = field?.tagEntries?.find(entry => entry.key === 'association')?.value;
                switch (association) {
                    case 'source1':
                        primaryAssociationKey = field.uid;
                        break;
                    case 'source2':
                        secondaryAssociationKey = field.uid;
                        break;
                    case 'main':
                        mainAssociationKey = field.uid;
                        break;
                }
            });
        const isMismactchGroup = this.misMatchEntityId === this.selectedEntity?.uid;
        let isDifferentGroup: boolean;
        records?.forEach(record => {
            const errors = record?.errors?.filter(error => !error?.match);
            const row: TableData = {
                recordId: record?.id,
                disabled: false,
                errors: errors as any,
                errorsCount: errors?.length,
                score: record?.['score'],
                hideSource1Checkbox: false,
                hideSource2Checkbox: false,
            };
            let isSource1Record: boolean, isSource2Record: boolean;
            if (isMismactchGroup) {
                isSource1Record = (record as Record).fields?.find(field => field.id === source1Key)?.value !== undefined;
                isSource2Record = (record as Record).fields?.find(field => field.id === source2Key)?.value !== undefined;
                this.selectedEntity.fields
                    ?.filter(field => (isSource1Record ? field.fieldSection === this.source1 : true))
                    ?.filter(field => (isSource2Record ? field.fieldSection === this.source2 : true))
                    .forEach(field => {
                        this.assignValue(
                            row,
                            field.uid,
                            field.datatype,
                            (record as Record).fields.find(fieldData => fieldData.id === field.uid)?.value,
                            field.valueDisplayFormat
                        );
                    });
                row.hideSource2Checkbox = isSource1Record;
                row.hideSource1Checkbox = isSource2Record;
            } else if (this.isGrouped) {
                const source1Value = record.fields.find(field => field.id === primaryAssociationKey)?.value;
                const source2Value = record.fields.find(field => field.id === secondaryAssociationKey)?.value;
                const mainValue = record.fields.find(field => field.id === mainAssociationKey)?.value as string;
                isDifferentGroup = !mainAssociationIds.includes(mainValue);
                isDifferentGroup && mainAssociationIds.push(mainValue);
                const isSource1Duplicated = source1AssociationIds.includes(source1Value);
                const isSource2Duplicated = source2AssociationIds.includes(source2Value);
                !isSource1Duplicated && source1Value && source1AssociationIds.push(source1Value);
                !isSource2Duplicated && source2Value && source2AssociationIds.push(source2Value);
                isSource1Record = !isSource1Duplicated && source1Value !== undefined;
                isSource2Record = !isSource2Duplicated && source2Value !== undefined;
                row.bgColor = ['#FFF', '#f3f5fe'][mainAssociationIds.indexOf(mainValue) % 2];
                row.mainAssociationId = mainValue;
                this.selectedEntity.fields
                    .filter(field => (isSource1Record && !isSource2Record ? field.fieldSection === this.source1 : true))
                    .filter(field => (isSource2Record && !isSource1Record ? field.fieldSection === this.source2 : true))
                    .forEach(field => {
                        this.assignValue(
                            row,
                            field.uid,
                            field.datatype,
                            (record as Record).fields.find(fieldData => fieldData.id === field.uid)?.value,
                            field.valueDisplayFormat
                        );
                    });
                row.hideSource1Checkbox = !isSource1Record;
                row.hideSource2Checkbox = !isSource2Record;
            } else {
                (isSource1Record = true), (isSource2Record = true);
                this.selectedEntity.fields?.forEach(field => {
                    this.assignValue(
                        row,
                        field.uid,
                        field.datatype,
                        (record as Record).fields.find(fieldData => fieldData.id === field.uid)?.value,
                        field.valueDisplayFormat
                    );
                });
            }
            row.selected = record.selected ?? false;
            this.isGrouped && this.pushDifference(source1Rows, source2Rows);
            isSource1Record && source1Rows.push(row);
            isSource2Record && source2Rows.push(row);
        });
        this.isGrouped && this.pushDifference(source1Rows, source2Rows);
        (!sourceType || sourceType === 'source1') && this.source1tableData.next(source1Rows);
        (!sourceType || sourceType === 'source2') && this.source2tableData.next(source2Rows);
        this.isConfigurationLoaded = true;
        this.prepareTotalCount();
    };

    private pushDifference = (rows1: TableData[], rows2: TableData[]) => {
        const count = rows1.length - rows2.length;
        if (count > 0) {
            CommonUtilsService.getRange(0, count - 1).forEach(() =>
                rows2.push({
                    hideSource2Checkbox: true,
                    recordId: undefined,
                    bgColor: rows1[rows1.length - 1].bgColor,
                })
            );
        }
        if (count < 0) {
            CommonUtilsService.getRange(0, Math.abs(count) - 1).forEach(() =>
                rows1.push({
                    hideSource1Checkbox: true,
                    recordId: undefined,
                    bgColor: rows2[rows2.length - 1].bgColor,
                })
            );
        }
    };

    private assignValue = (row: TableData, id: string, dataType: string, value: any, valueDisplayFormat?: string) => {
        switch (dataType) {
            case 'INT':
            case 'LONG':
            case 'FLOAT':
            case 'DOUBLE':
            case 'DECIMAL':
                row[id] = CommonUtilsService.formatNumber(value, valueDisplayFormat);
                break;
            default:
                row[id] = value;
                break;
        }
    };

    private onEntitySelectionChange = () => {
        if (this.selectedEntity?.uid !== this.previousSelectedEntityUid) {
            this.selectedTable1Id = '';
            this.selectedTable2Id = '';
            this.previousSelectedEntityUid = this.selectedEntity?.uid;
        }
        this.clearRecordsPagination();
        this.prepareTableColumns();
        this.getSelectedEntityRecords();
    };

    private prepareTableColumns = () => {
        this.resetSelection();
        const source1allColumns = [];
        const source2allColumns = [];
        this.source1_selectedColumns = [];
        this.source2_selectedColumns = [];
        if (this.movableEntityIds?.includes(this.selectedEntity?.uid)) {
            !this.demoMode && source1allColumns.push(this.addCheckboxColumn());
            !this.demoMode && source2allColumns.push(this.addCheckboxColumn());
        }
        this.selectedEntity.fields.forEach(field => {
            if (this.source1 && field?.fieldSection === this.source1) {
                source1allColumns.push(this.prepareColumnDetails(field));
                this.source1_selectedColumns.length < 4 && this.source1_selectedColumns.push(field.uid);
            } else if (this.source2 && field?.fieldSection === this.source2) {
                source2allColumns.push(this.prepareColumnDetails(field));
                this.source2_selectedColumns.length < 4 && this.source2_selectedColumns.push(field.uid);
            }
        });

        // Load saved column preferences
        this.loadColumnPreferences(source1allColumns, source2allColumns);

        this.source1_allColumns.next(source1allColumns);
        this.source2_allColumns.next(source2allColumns);
        this.source1_allColumns.pipe(takeUntil(this.unSubscribe)).subscribe(columns => {
            this.table1NumericFields = [];
            const numericColumns = columns?.filter(column => ['DOUBLE', 'DECIMAL', 'FLOAT', 'INTEGER'].includes(column?.inputType)) || [];

            this.table1NumericFields.push(...numericColumns);
        });
        this.source2_allColumns.pipe(takeUntil(this.unSubscribe)).subscribe(columns => {
            this.table2NumericFields = [];
            const numericColumns = columns?.filter(column => ['DOUBLE', 'DECIMAL', 'FLOAT', 'INTEGER'].includes(column?.inputType)) || [];

            this.table2NumericFields.push(...numericColumns);
        });

        // Set default to first numeric field (Document Value) if no preference exists
        if (!this.selectedTable1Id && this.table1NumericFields.length > 0) {
            this.selectedTable1Id = this.table1NumericFields[0].id;
        }
        if (!this.selectedTable2Id && this.table2NumericFields.length > 0) {
            this.selectedTable2Id = this.table2NumericFields[0].id;
        }
    };

    private prepareColumnDetails = (field: Field) => {
        return {
            hide: false,
            icon: undefined,
            id: field?.uid,
            name: field?.displayName || field?.name,
            type: 'text',
            searchable: field?.searchable,
            inputType: field?.datatype,
        };
    };

    /**
     * Save column preferences to localStorage
     * Key format: apps.[appRestApiName].reconciliation.table.fields
     * Value format: { [fieldName]: { selected: boolean, order: number } }
     */
    private saveColumnPreferences = (source: 'source1' | 'source2', columns: TableColumn[]) => {
        const appRestApiName = this.assetMetaData?.restAPIName || 'default';
        const entity = this.selectedEntity?.uid;

        if (!entity) {
            // No entity available, skip saving preferences
            return;
        }

        const key = `apps.${appRestApiName}.reconciliation.${entity}.${source}.table.fields`;

        const fieldsConfig: { [key: string]: { selected: boolean; order: number } } = {};
        const selectedColumns = source === 'source1' ? this.source1_selectedColumns : this.source2_selectedColumns;
        let orderIndex = 0;
        columns.forEach(column => {
            // Skip internal table columns and only save selected fields
            if (column.id !== 'SELECTRECORD' && selectedColumns.includes(column.id)) {
                fieldsConfig[column.id] = {
                    selected: true,
                    order: orderIndex++,
                };
            }
        });

        this.userPreferences.setPreference(key, fieldsConfig);
    };

    /**
     * Load column preferences from localStorage
     */
    private loadColumnPreferences = (source1Columns: TableColumn[], source2Columns: TableColumn[]) => {
        const appRestApiName = this.assetMetaData?.restAPIName || 'default';
        const entity = this.selectedEntity?.uid;

        const key1 = `apps.${appRestApiName}.reconciliation.${entity}.source1.table.fields`;
        const key2 = `apps.${appRestApiName}.reconciliation.${entity}.source2.table.fields`;
        const totalColumnKey1 = `apps.${appRestApiName}.reconciliation.${entity}.source1.aggregation`;
        const totalColumnKey2 = `apps.${appRestApiName}.reconciliation.${entity}.source2.aggregation`;

        const config1 = this.userPreferences.getPreference<{ [key: string]: any }>(key1, {});
        const config2 = this.userPreferences.getPreference<{ [key: string]: any }>(key2, {});

        // Load selected column for total from preferences
        this.selectedTable1Id = this.userPreferences.getPreference<string>(totalColumnKey1);
        this.selectedTable2Id = this.userPreferences.getPreference<string>(totalColumnKey2);

        if (config1 && Object.keys(config1).length > 0) {
            this.applyColumnPreferences(source1Columns, config1);
            // Build selectedColumns from preferences - only include columns with selected: true
            this.source1_selectedColumns = source1Columns
                .filter(col => col.id !== 'SELECTRECORD' && config1[col.id]?.selected)
                .sort((a, b) => (config1[a.id]?.order ?? 999) - (config1[b.id]?.order ?? 999))
                .map(col => col.id);
        } else {
            // Use default behavior - select all selected columns
            this.source1_selectedColumns = source1Columns.filter(col => !col.hide && col.id !== 'SELECTRECORD').map(col => col.id);
        }

        if (config2 && Object.keys(config2).length > 0) {
            this.applyColumnPreferences(source2Columns, config2);
            // Build selectedColumns from preferences - only include columns with selected: true
            this.source2_selectedColumns = source2Columns
                .filter(col => col.id !== 'SELECTRECORD' && config2[col.id]?.selected)
                .sort((a, b) => (config2[a.id]?.order ?? 999) - (config2[b.id]?.order ?? 999))
                .map(col => col.id);
        } else {
            // Use default behavior - select all selected columns
            this.source2_selectedColumns = source2Columns.filter(col => !col.hide && col.id !== 'SELECTRECORD').map(col => col.id);
        }
    };

    /**
     * Apply saved preferences to columns array
     */
    private applyColumnPreferences = (columns: TableColumn[], preferences: { [key: string]: { selected: boolean; order: number } }) => {
        columns.forEach(column => {
            if (column.id !== 'SELECTRECORD' && preferences[column.id]) {
                column.hide = !preferences[column.id].selected;
            }
        });

        // Sort columns by order
        columns.sort((a, b) => {
            const orderA = preferences[a.id]?.order ?? 999;
            const orderB = preferences[b.id]?.order ?? 999;
            return orderA - orderB;
        });
    };

    /**
     * Call this when user changes column visibility or order
     */
    public onColumnChange = (source: 'source1' | 'source2') => {
        const columns = source === 'source1' ? this.source1_allColumns.value : this.source2_allColumns.value;
        this.saveColumnPreferences(source, columns);
    };

    /**
     * Handle column change event from table component
     */
    public onColumnChangeFromTable = (event: { columns: string[]; source: string }) => {
        const source = event.source === 'source1' ? 'source1' : 'source2';
        const columns = source === 'source1' ? this.source1_allColumns.value : this.source2_allColumns.value;

        // Internal columns to preserve
        const internalColumns = ['SELECTRECORD', 'table-actions', 'log', 'table-checkbox'];
        const preservedInternalColumns = columns.filter(col => internalColumns.includes(col.id));

        // Update column visibility based on selected columns
        columns.forEach(column => {
            column.hide = !event.columns.includes(column.id) && !internalColumns.includes(column.id);
        });

        // Reorder columns: selected columns first (in selected order), then hidden columns, then internal columns
        const visibleColumns = event.columns.map(colId => columns.find(col => col.id === colId)).filter(col => col !== undefined);
        const hiddenColumns = columns.filter(col => col.hide && !internalColumns.includes(col.id));
        const reorderedColumns = [...visibleColumns, ...hiddenColumns, ...preservedInternalColumns];

        if (source === 'source1') {
            this.source1_allColumns.next(reorderedColumns);
            // Create new array reference to trigger change detection
            this.source1_selectedColumns = [...event.columns];
        } else {
            this.source2_allColumns.next(reorderedColumns);
            // Create new array reference to trigger change detection
            this.source2_selectedColumns = [...event.columns];
        }

        // Save to UserPreferencesService
        this.saveColumnPreferences(source as 'source1' | 'source2', reorderedColumns);

        // Trigger column matching dialog after column preferences are applied
        this.triggerColumnMatchingAfterApply(source);
    };

    private triggerColumnMatchingAfterApply(source: 'source1' | 'source2'): void {
        // Prevent triggering if we're currently applying matched columns (to avoid infinite loop)
        if (this.isApplyingMatchedColumns) {
            return;
        }

        // Get the selected columns from the table (not the aggregation dropdown)
        const selectedColumns = source === 'source1' ? this.source1_selectedColumns : this.source2_selectedColumns;

        if (!selectedColumns || selectedColumns.length === 0) {
            return;
        }

        // Open column matching dialog once with all selected columns
        this.openColumnMatchingDialog(source);
    }

    /**
     * Reset column preferences to default
     */
    public resetColumnPreferences = (source: 'source1' | 'source2') => {
        const appRestApiName = this.assetMetaData?.restAPIName || 'default';
        const entity = this.selectedEntity?.uid;

        if (!entity) {
            return;
        }

        const key = `apps.${appRestApiName}.reconciliation.${entity}.${source}.table.fields`;

        // Remove the preference from userPreferences
        this.userPreferences.removePreference(key);

        // Rebuild default columns
        this.prepareTableColumns();
    };

    public openColumnMatchingDialog(source: 'source1' | 'source2'): void {
        const selectedColumns = (source === 'source1' ? this.source1_allColumns : this.source2_allColumns).value.filter(col =>
            (source === 'source1' ? this.source1_selectedColumns : this.source2_selectedColumns).includes(col.id)
        );

        if (!selectedColumns || selectedColumns.length === 0) {
            return;
        }

        const oppositeColumns = (source === 'source1' ? this.source2_allColumns : this.source1_allColumns).value.filter(
            col => col.id !== 'SELECTRECORD' && !col.hideInAllColumnsList
        );

        const dialogRef = this.dialog.open(ColumnMatchingDialogComponent, {
            width: '600px',
            maxWidth: '90vw',
            data: {
                selectedColumns,
                oppositeColumns,
                sourceSide: source,
            } as ColumnMatchingDialogData,
        });

        dialogRef.afterClosed().subscribe(matchedColumns => {
            if (matchedColumns && matchedColumns.length > 0) {
                this.applyMatchedColumns(source, matchedColumns);
            }
        });
    }

    private applyMatchedColumns(source: 'source1' | 'source2', matchedColumns: TableColumn[]): void {
        const targetSource = source === 'source1' ? 'source2' : 'source1';
        const targetColumns = targetSource === 'source1' ? this.source1_allColumns : this.source2_allColumns;

        // Replace selected columns with matched columns only
        const newSelectedColumns = matchedColumns.map(col => col.id);

        // Update selected columns array
        if (targetSource === 'source1') {
            this.source1_selectedColumns = [...newSelectedColumns];
        } else {
            this.source2_selectedColumns = [...newSelectedColumns];
        }

        // Ensure matched columns are visible and hide others
        targetColumns.value.forEach(column => {
            column.hide = !newSelectedColumns.includes(column.id) && column.id !== 'SELECTRECORD';
        });

        // Set flag to prevent infinite loop
        this.isApplyingMatchedColumns = true;

        // Trigger column change to update the table
        this.onColumnChangeFromTable({
            columns: newSelectedColumns,
            source: targetSource,
        });

        // Reset flag after a short delay to ensure column change is processed
        setTimeout(() => {
            this.isApplyingMatchedColumns = false;
        }, 100);
    }

    private sanitizeCsvValue = (value: any): string => {
        let stringValue = value === undefined || value === null ? '' : String(value);
        // Neutralize CSV formula injection when the file is opened in spreadsheet software
        if (/^[=+\-@]/.test(stringValue)) {
            stringValue = `'${stringValue}`;
        }
        return /["\n,]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
    };

    public exportTableData = (source: 'source1' | 'source2', mode: 'selected' | 'all') => {
        if (mode === 'selected') {
            this.exportRows(source, this.buildSelectedExportRows(source), mode);
            return;
        }
        this.exportAllRecords(source);
    };

    /**
     * Selected rows may span pages the user is no longer viewing. pageIndexVsRecords already
     * accumulates the per-page selected TableData (see prepareIndexVsSelectedRecords / getSelectedTableData),
     * so it's the single source of truth for "everything the user checked" across pagination.
     */
    private buildSelectedExportRows = (source: 'source1' | 'source2'): TableData[] => {
        this.prepareIndexVsSelectedRecords();
        let rows: TableData[] = [];
        Object.values(this.pageIndexVsRecords[source]).forEach(pageEntry => {
            rows = [...rows, ...pageEntry.tableData];
        });
        return rows.filter(row => row.recordId !== undefined);
    };

    private exportAllRecords = (source: 'source1' | 'source2') => {
        // Prevent duplicate exports while one is already in progress
        if (this.isExporting[source]) {
            return;
        }

        const totalCount = this.dataLength;

        if (!totalCount || totalCount === 0) {
            this._utils.alertError(translate('No records available to export.'));
            return;
        }

        const obj = {};
        for (let i = 0; i < this.searchedColumnsData.length; i++) {
            this.searchedColumnsData[i].value && (obj[this.searchedColumnsData[i].name] = this.searchedColumnsData[i].value);
        }

        const criteria = this.prepareCriteria(obj);

        const entityId = this.selectedEntity?.uid;

        // Page size for each background fetch: large enough to minimise round trips
        // but within typical backend limits (e.g. 200).
        const PAGE_SIZE = 200;

        this.isExporting[source] = true;

        // Use RxJS expand to paginate through all records without a mutable
        // accumulator. Each emission is { records, searchAfter, done }.
        // expand recurses until done === true, then EMPTY terminates the chain.
        this._api.records
            .searchEntityRecordsObservable({
                assetName: this.app?.serviceName,
                query: { ...criteria },
                searchAfter: undefined,
                searchEntityId: entityId,
                restApiServiceName: this.app?.restApiName,
                size: PAGE_SIZE,
                noAlerts: false,
            })
            .pipe(
                // expand re-invokes itself with the next page cursor until the
                // API returns fewer than PAGE_SIZE records or no searchAfter cursor.
                expand((response: any) => {
                    const fetched: any[] = response?.records || [];
                    const nextCursor = response?.searchAfter ?? null;
                    const hasMore = nextCursor && fetched.length === PAGE_SIZE;

                    if (!hasMore) {
                        return EMPTY;
                    }

                    return this._api.records.searchEntityRecordsObservable({
                        assetName: this.app?.serviceName,
                        query: { ...criteria },
                        searchAfter: nextCursor,
                        searchEntityId: entityId,
                        restApiServiceName: this.app?.restApiName,
                        size: PAGE_SIZE,
                        noAlerts: false,
                    });
                }),
                // Collect every page's records into one flat array
                reduce((all: any[], response: any) => [...all, ...(response?.records || [])], []),
                takeUntil(this.unSubscribe),
                catchError(() => {
                    this._utils.alertError(translate('Failed to export records. Please try again.'));
                    this.isExporting[source] = false;
                    return EMPTY;
                })
            )
            .subscribe((allRecords: any[]) => {
                this.isExporting[source] = false;
                this.exportFetchedRecords(source, allRecords);
            });
    };

    private exportFetchedRecords = (source: 'source1' | 'source2', records: any[]) => {
        const entity = this.selectedEntity;

        const rowsToExport: TableData[] = records.map(responseRecord => {
            const record = new Record(responseRecord, entity);
            const errors = record?.errors?.filter(error => !error?.match);

            const row: TableData = {
                recordId: record?.id,
                disabled: false,
                errors: errors as any,
                errorsCount: errors?.length,
                score: record?.['score'],
                hideSource1Checkbox: false,
                hideSource2Checkbox: false,
            };

            entity?.fields?.forEach(field => {
                this.assignValue(
                    row,
                    field.uid,
                    field.datatype,
                    record.fields.find(f => f.id === field.uid)?.value,
                    field.valueDisplayFormat
                );
            });

            return row;
        });

        this.exportRows(source, rowsToExport, 'all');
    };

    private exportRows = (source: 'source1' | 'source2', rows: TableData[], mode: 'selected' | 'all') => {
        const allColumns = (source === 'source1' ? this.source1_allColumns : this.source2_allColumns).value;
        const selectedColumnIds = source === 'source1' ? this.source1_selectedColumns : this.source2_selectedColumns;
        const columns = selectedColumnIds.map(id => allColumns.find(col => col.id === id)).filter(col => !!col);

        const exportableRows = rows.filter(row => row.recordId !== undefined);

        if (columns.length === 0 || exportableRows.length === 0) {
            this._utils.alertError(
                mode === 'selected'
                    ? translate('Please select at least one record to export.')
                    : translate('No records available to export.')
            );
            return;
        }

        const csvLines = [
            columns.map(col => this.sanitizeCsvValue(col.displayName || col.name)).join(','),
            ...exportableRows.map(row => columns.map(col => this.sanitizeCsvValue(row[col.id])).join(',')),
        ];
        const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const sourceName = (source === 'source1' ? this.source1 : this.source2) || source;
        const link = document.createElement('a');
        link.href = url;
        link.download = `${sourceName}_${mode}_records.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    private getSelectedEntityRecords = (source?: 'source1' | 'source2') => {
        this.store$.dispatch(
            GetFilingInstanceEntityRecords({
                reconSpecific: this.isMisMatchGroup || this.isGrouped,
                source: source || (this.isGrouped ? 'grouped' : undefined),
            })
        );
    };

    public clickedRecord = (record: TableData) => {
        this.store$.dispatch(
            SetFilingInstanceEntityView({
                recordId: record.recordId,
                viewLevel: this.currentView?.level,
                entityId: this.currentView?.entityId,
            })
        );
    };

    public changedPage = (event: PageEvent, sourceType?: 'source1' | 'source2') => {
        this.prepareIndexVsSelectedRecords();
        this.prepareTotalCount();
        switch (sourceType) {
            case 'source1':
                this.store$.dispatch(
                    SetFilingInstanceEntityPagination({
                        pageIndex: this.source1Pagination.size !== event?.pageSize ? 0 : event?.pageIndex,
                        pageSize: event?.pageSize,
                        reconSpecific: sourceType,
                        entityId: undefined,
                    })
                );
                break;
            case 'source2':
                this.store$.dispatch(
                    SetFilingInstanceEntityPagination({
                        pageIndex: this.source2Pagination.size !== event?.pageSize ? 0 : event?.pageIndex,
                        pageSize: event?.pageSize,
                        reconSpecific: sourceType,
                        entityId: undefined,
                    })
                );
                break;
            default:
                this.store$.dispatch(
                    SetFilingInstanceEntityPagination({
                        pageIndex: this.source1Pagination.size !== event?.pageSize ? 0 : event?.pageIndex,
                        pageSize: event?.pageSize,
                        reconSpecific: this.isGrouped ? 'grouped' : undefined,
                        entityId: undefined,
                    })
                );
                break;
        }
    };

    showSelectedRecords = () => {
        const dialogRef = this.dialog.open(InstanceDataDialogComponent, {
            panelClass: ['instanceDialog'],
            disableClose: false,
            width: '95vw',
            height: '95vh',
            maxWidth: '100vw',
        });
        dialogRef.componentInstance.demoMode = true;
        dialogRef.componentInstance.dialogRef = dialogRef;
        dialogRef.componentInstance.source1 = this.source1;
        dialogRef.componentInstance.source2 = this.source2;
        dialogRef.componentInstance.selectedEntity = this.selectedEntity;
        dialogRef.componentInstance.entityRecords = this.getSelectedRecords();
        dialogRef.componentInstance.isMisMatchGroup = this.isMisMatchGroup;
        dialogRef.componentInstance.misMatchEntityId = this.misMatchEntityId;
        dialogRef.componentInstance.isGrouped = this.isGrouped;
    };

    private getSelectedRecords = () => {
        const records: {
            source1: Record[];
            source2: Record[];
        } = {
            source1: [],
            source2: [],
        };
        Object.keys(this.pageIndexVsRecords).forEach(source => {
            Object.keys(this.pageIndexVsRecords[source]).forEach(row => {
                this.pageIndexVsRecords[source][row as any as number].records.forEach(record => {
                    records[source].push(record);
                });
            });
        });
        return records;
    };

    private prepareIndexVsSelectedRecords = () => {
        const records = [...this.source1tableData.value, ...this.source2tableData.value];
        const ids = records
            .filter(row => row.selected)
            .reduce((ids, row) => {
                if (!ids.includes(row.recordId)) {
                    ids.push(row.recordId);
                }
                return ids;
            }, []);

        if (!this.pageIndexVsRecords.source1[this.source1Pagination.index]) {
            this.pageIndexVsRecords.source1[this.source1Pagination.index] = {
                records: [],
                tableData: [],
            };
        }
        if (!this.pageIndexVsRecords.source2[this.source2Pagination.index]) {
            this.pageIndexVsRecords.source2[this.source2Pagination.index] = {
                records: [],
                tableData: [],
            };
        }

        switch (this.isMisMatchGroup) {
            case true:
                this.pageIndexVsRecords.source1[this.source1Pagination.index] = {
                    records: this.entityRecords.source1.filter(record => ids.includes(record.id)),
                    tableData: this.source1tableData.value.filter(row => row.selected && row.recordId),
                };
                this.pageIndexVsRecords.source2[this.source2Pagination.index] = {
                    records: this.entityRecords.source2.filter(record => ids.includes(record.id)),
                    tableData: this.source2tableData.value.filter(row => row.selected && row.recordId),
                };
                break;
            default:
                this.pageIndexVsRecords.source1[this.source1Pagination.index] = {
                    tableData: this.source1tableData.value.filter(row => row.selected && row.recordId),
                    records: this.entityRecords.source1
                        .filter(record => ids.includes(record.id))
                        .filter(record => !!this.source1tableData.value.find(row => record.id === row.recordId)),
                };
                this.pageIndexVsRecords.source2[this.source2Pagination.index] = {
                    tableData: this.source2tableData.value.filter(row => row.selected && row.recordId),
                    records: this.entityRecords.source1
                        .filter(record => ids.includes(record.id))
                        .filter(record => !!this.source2tableData.value.find(row => record.id === row.recordId)),
                };
                break;
        }
    };

    private makeAllRecordsUnSelected = () => {
        const source1Data = this.source1tableData.value.map(obj => ({ ...obj, selected: false }));
        this.source1tableData.next(source1Data);
        const source2Data = this.source2tableData.value.map(obj => ({ ...obj, selected: false }));
        this.source2tableData.next(source2Data);
    };

    private resetSelection = () => {
        this.pageIndexVsRecords = {
            source1: {},
            source2: {},
        };
        this.makeAllRecordsUnSelected();
        this.selectedRecordsCount = 0;
    };

    public initiateChange = () => {
        this.prepareIndexVsSelectedRecords();
        this.prepareTotalCount();
        if (this.validateTolerances(this.applySelectedRecordChange)) {
            this.applySelectedRecordChange();
        }
    };

    private applySelectedRecordChange = () => {
        const tableRecords = this.getSelectedTableData();
        const recordsSplit = this.getSelectedRecords();
        const entityRecords = [];
        Object.values(recordsSplit)?.forEach(records => {
            records.filter(record => !entityRecords.find(item => item.id === record.id)).forEach(record => entityRecords.push(record));
        });
        const payload: RecordPayload[] = [];
        const moveToEntity = this.entities.find(entity => entity.uid === this.moveToGroup);
        const primaryField = moveToEntity?.businessKeys?.fields?.[0];
        const moveToEntityIdentifier = this.getEntityIdentifier(this.moveToGroup, primaryField);
        const currentEntityIdentifier = this.getEntityIdentifier(this.selectedEntity?.uid, this.selectedEntity?.businessKeys?.fields?.[0]);
        this.recordIdsToMove = [];
        let source1Index = 0,
            source2Index = 1;
        const associationIds = [];
        const source1Associationkey = moveToEntity?.fields?.find(field =>
            field.tagEntries?.find(tag => tag.key === 'association' && tag.value === 'source1')
        )?.uid;
        const source2Associationkey = moveToEntity?.fields?.find(field =>
            field.tagEntries?.find(tag => tag.key === 'association' && tag.value === 'source2')
        )?.uid;
        tableRecords.forEach(row => {
            if (!row.selected) {
                return;
            }
            this.recordIdsToMove.push(row.recordId);
            const record = entityRecords.find(recordItem => recordItem.id === row.recordId);
            const recordPayload = new RecordPayload(record);
            const fieldsToSplice = [];
            recordPayload.fields.forEach((field, index) => {
                let id = field.id;
                if (currentEntityIdentifier.length > 0) {
                    id = id.substring(0, id.lastIndexOf(currentEntityIdentifier));
                }
                if (moveToEntityIdentifier.length > 0) {
                    id += moveToEntityIdentifier;
                }
                const fieldExistsInDestination = moveToEntity.fields.find(destField => destField.uid === id);
                if (fieldExistsInDestination) {
                    field.id = id;
                    return;
                }
                fieldsToSplice.push(index);
            });
            fieldsToSplice.reverse().forEach(index => {
                recordPayload.fields.splice(index, 1);
            });
            delete recordPayload.id;
            delete recordPayload.parentRecordId;
            payload.push(recordPayload);
            if (row.hideSource2Checkbox) {
                source1Index = payload.length - 1;
            } else if (row.hideSource1Checkbox) {
                source2Index = payload.length - 1;
            }
        });
        if (this.isMisMatchGroup) {
            const source1 = payload[source1Index];
            const source2 = payload[source2Index];
            payload.splice(0);
            moveToEntity.fields
                ?.filter(field => field.fieldSection === this.source2)
                .forEach(field => {
                    const foundField = source1.fields.find(sourceField => sourceField.id === field.uid);
                    const source2Field = source2.fields.find(sourceField => sourceField.id === field.uid);
                    if (foundField) {
                        foundField.value = source2Field?.value;
                    } else if (source2Field) {
                        source1.fields.push(source2Field);
                    }
                });
            const statusField = moveToEntity.fields.find(field => field.tagEntries.find(entry => entry.value === 'mark_status'));
            if (statusField) {
                const fieldStatus = statusField.tagEntries?.find(entry => entry.key === 'status_marked')?.value;
                const recordStatusField = source1.fields.find(sourceField => sourceField.id === statusField.uid);
                if (recordStatusField) {
                    recordStatusField.value = fieldStatus;
                } else {
                    source1.fields.push({
                        id: statusField?.uid,
                        value: fieldStatus,
                    });
                }
            }
            payload.push(source1);
        } else {
            const newPayload = [];
            const unique = moveToEntity.fields.find(field => field.tagEntries?.find(entry => entry.value === 'unique-field'));
            payload.forEach(record => {
                moveToEntity.businessKeys.fields.forEach(fieldId => {
                    const mandatoryField = record.fields.find(field => field.id === fieldId);
                    const id = this._utils.guid();
                    if (!mandatoryField) {
                        record.fields.push({
                            id: fieldId,
                            value: id,
                        });
                    } else if (!mandatoryField?.value) {
                        mandatoryField.value = id;
                    }
                });
                const source1Record: RecordPayload = CommonUtilsService.cloneObject(record);
                const source2Record: RecordPayload = CommonUtilsService.cloneObject(record);
                const uniqueField = source2Record.fields.find(field => field.id === unique.uid);
                uniqueField.value += '_SOURCE2';
                moveToEntity.fields.forEach(field => {
                    if (field.fieldSection !== this.source2) {
                        return;
                    }
                    const index = source1Record.fields.findIndex(
                        sourceField => sourceField?.id === field.uid && !field.isBusinessKey && !field.mandatory
                    );
                    if (index > -1) {
                        source1Record.fields[index] = undefined;
                    }
                });
                moveToEntity.fields.forEach(field => {
                    if (field.fieldSection !== this.source1) {
                        return;
                    }
                    const index = source2Record.fields.findIndex(
                        sourceField => sourceField?.id === field.uid && !field.isBusinessKey && !field.mandatory
                    );
                    if (index > -1) {
                        source2Record.fields[index] = undefined;
                    }
                });
                source1Record.fields = source1Record.fields.filter(field => field !== undefined);
                source2Record.fields = source2Record.fields.filter(field => field !== undefined);
                const source1AssocationValue =
                    source1Associationkey && source1Record.fields.find(field => field.id === source1Associationkey)?.value;
                const source1KeyExists = source1AssocationValue && associationIds.includes(source1AssocationValue);
                source1AssocationValue && !source1KeyExists && associationIds.push(source1AssocationValue);
                const source2AssocationValue =
                    source2Associationkey && source2Record.fields.find(field => field.id === source2Associationkey)?.value;
                const source2KeyExists = source2AssocationValue && associationIds.includes(source2AssocationValue);
                source2AssocationValue && !source2KeyExists && associationIds.push(source2AssocationValue);
                const statusField = moveToEntity.fields.find(field => field.tagEntries.find(entry => entry.value === 'mark_status'));
                const fieldStatus = statusField.tagEntries?.find(entry => entry.key === 'status_marked')?.value;
                const source1RecordStatusField = source1Record.fields.find(sourceField => sourceField.id === statusField.uid);
                if (fieldStatus && source1RecordStatusField) {
                    source1RecordStatusField.value = fieldStatus;
                } else if (fieldStatus) {
                    source1Record.fields.push({
                        id: statusField?.uid,
                        value: fieldStatus,
                    });
                }
                const source2RecordStatusField = source2Record.fields.find(sourceField => sourceField.id === statusField.uid);
                if (fieldStatus && source2RecordStatusField) {
                    source2RecordStatusField.value = fieldStatus;
                } else if (fieldStatus) {
                    source2Record.fields.push({
                        id: statusField?.uid,
                        value: fieldStatus,
                    });
                }
                (this.isGrouped ? !source1KeyExists : true) && newPayload.push(source1Record);
                (this.isGrouped ? !source2KeyExists : true) && newPayload.push(source2Record);
            });
            payload.splice(0, payload.length, ...newPayload);
        }
        this.store$.dispatch(
            SaveRecords({
                entityUid: this.moveToGroup,
                instanceId: this.instanceId,
                parentRecordId: undefined,
                recordsPayload: payload,
                serviceId: this.serviceId,
                deleteFromSource: true,
                noMessages: true,
            })
        );
    };

    private getEntityIdentifier = (entityId: string, fieldId: string) => {
        let keyToAppend = '';
        const reverseEntityId = entityId.split('').reverse();
        let canContinue = true;
        fieldId
            ?.split('')
            .reverse()
            .forEach((character, index) => {
                canContinue = canContinue && character === reverseEntityId[index];
                if (!canContinue) {
                    return;
                }
                keyToAppend = character + keyToAppend;
            });
        keyToAppend = keyToAppend.substring(keyToAppend.indexOf('_'));
        if (keyToAppend[0] !== '_' && keyToAppend !== entityId) {
            keyToAppend = '';
        }
        return keyToAppend;
    };

    private deleteFromSource = () => {
        const payload: {
            id: string;
            parentRecordId: string;
        }[] = [];
        const entityRecords = this.getSelectedRecords();
        Object.values(entityRecords).forEach(records => {
            records.forEach(record =>
                payload.push({
                    id: record.id,
                    parentRecordId: record.parentId,
                })
            );
        });
        this.recordIdsToMove = [];
        this.store$.dispatch(
            DeleteRecords({
                entityId: this.selectedEntity?.uid,
                instanceId: this.instanceId,
                parentRecordId: undefined,
                serviceId: this.serviceId,
                payload,
                noMessages: true,
                deleteFromSource: true,
                reconSpecific: this.isMisMatchGroup,
            })
        );
    };

    public onColumnSearch = (data: any, source?: 'source1' | 'source2') => {
        const clonedData = CommonUtilsService.cloneObject(data);
        const field = this.selectedEntity.fields.find(field => field.uid === clonedData?.name);
        if (field.datatype === 'DATE' && clonedData?.value) {
            const dateCheck = CommonUtilsService.transformDate(clonedData.value as string, field.outputFormat as any, 'dd-mm-yyyy');
            if (!dateCheck || dateCheck.includes('undefined') || dateCheck.includes('null')) {
                this._utils.alertError('Enter valid date');
                return;
            } else {
                clonedData.value = dateCheck;
            }
        }
        const columnExists = this.searchedColumnsData.findIndex(c => c.name === clonedData.name);
        columnExists === -1 && this.searchedColumnsData.push(clonedData);
        for (let j = 0; j < this.searchedColumnsData.length; j++) {
            if (
                this.source1_selectedColumns.indexOf(this.searchedColumnsData?.[j]?.name) === -1 &&
                this.source2_selectedColumns.indexOf(this.searchedColumnsData?.[j]?.name) === -1
            ) {
                this.searchedColumnsData?.splice(j, 1);
            }
        }
        const obj = {};
        for (let i = 0; i < this.searchedColumnsData.length; i++) {
            if (this.searchedColumnsData[i].name === clonedData.name) {
                this.searchedColumnsData[i].value = clonedData.value;
                this.searchedColumnsData[i].value && (obj[this.searchedColumnsData[i].name] = this.searchedColumnsData[i].value);
            } else {
                this.searchedColumnsData[i].value && (obj[this.searchedColumnsData[i].name] = this.searchedColumnsData[i].value);
            }
        }
        this.store$.dispatch(
            ClearRecordsPagination({
                organizationId: this.organizationId,
                serviceId: this.serviceId,
                instanceId: this.instanceId,
                entityUid: this.selectedEntity.uid,
                parentRecordId: undefined,
                reconSpecific: source,
            })
        );
        if (this._utils.isEmpty(obj)) {
            if (this.isMisMatchGroup) {
                this.createCriteria(source);
            }
            this.getSelectedEntityRecords(source);
        } else {
            const criteria = this.prepareCriteria(
                obj,
                source ? { name: source === 'source1' ? this.source1 : this.source2, source } : undefined
            );
            this.store$.dispatch(
                SetFilingInstanceEntityCriteria({
                    source: this.isGrouped ? 'grouped' : source,
                    criteria,
                    entityId: undefined,
                })
            );
            this.store$.dispatch(
                GetFilingInstanceEntityRecords({
                    reconSpecific: this.isMisMatchGroup || this.isGrouped,
                    source: source || (this.isGrouped ? 'grouped' : undefined),
                })
            );
        }
    };

    public onSelectedColumnChange(source: 'source1' | 'source2', columnId: string | null) {
        if (source === 'source1') {
            this.selectedTable1Id = columnId;
        } else {
            this.selectedTable2Id = columnId;
        }

        // Save selected column for total to user preferences
        const appRestApiName = this.assetMetaData?.restAPIName || 'default';
        const entity = this.selectedEntity?.uid;

        if (entity) {
            const totalColumnKey1 = `apps.${appRestApiName}.reconciliation.${entity}.source1.aggregation`;
            const totalColumnKey2 = `apps.${appRestApiName}.reconciliation.${entity}.source2.aggregation`;

            if (source === 'source1') {
                this.userPreferences.setPreference(totalColumnKey1, columnId);
            } else {
                this.userPreferences.setPreference(totalColumnKey2, columnId);
            }
        }

        this.prepareTotalCount();
    }

    public prepareTotalCount = () => {
        const selectedRecords = this.getSelectedRecords();
        const total1 = selectedRecords.source1?.reduce((total, record) => {
            return total + CommonUtilsService.toNumber(record.fields.find(field => field.id === this.selectedTable1Id)?.value);
        }, 0);

        this.table1_totalCount = CommonUtilsService.formatNumber(total1, '#,###,###.00');

        const total2 = selectedRecords.source2?.reduce((total, record) => {
            return total + CommonUtilsService.toNumber(record.fields.find(field => field.id === this.selectedTable2Id)?.value);
        }, 0);

        this.table2_totalCount = CommonUtilsService.formatNumber(total2, '#,###,###.00');

        const variance = total1 - total2;
        this.variance_totalCount = CommonUtilsService.formatNumber(variance, '#,###,###.00');
    };

    private prepareCriteria = (obj: any, sourceTable?: { source: string; name: string }) => {
        let size: number;
        switch (sourceTable?.source) {
            case 'source2':
                size = this.source2Pagination.size;
                break;
            default:
                size = this.source1Pagination.size;
                break;
        }
        return {
            entityId: this.selectedEntity?.uid,
            restApiServiceName: this.assetMetaData.restAPIName,
            filterCriteria: {
                serviceId: this.serviceId,
                entityFilterCriterias: [
                    {
                        entityUid: this.selectedEntity?.uid,
                        criteriaDefinitions: [
                            {
                                type: 'CriteriaDefinition',
                                criteriaName: this.selectedEntity?.uid,
                                restriction: {
                                    condition: 'AND',
                                    rules: this.prepareRules(obj, sourceTable),
                                    restrictionType: 'JoinedRestrictionMetadata',
                                },
                            },
                        ],
                    },
                ],
                criteriaRelations: {
                    condition: 'AND',
                    rules: [
                        {
                            operator: 'EQ',
                            value: this.selectedEntity.uid,
                            fieldId: 'Criteria',
                            restrictionType: 'SingleRestrictionMetadata',
                        }
                    ],
                    restrictionType: 'JoinedRestrictionMetadata',
                },
            },
            size,
            sort: {},
        };
    };

    private prepareRules = (obj: any, sourceTable?: { source: string; name: string }) => {
        const rules = [{
            fieldId: 'assetDataId',
            operator: 'EQ',
            value: this.instanceId,
            restrictionType: 'SingleRestrictionMetadata',
        }];
        let sourceFieldId: string;
        let groupFieldId: string;
        if (sourceTable) {
            const field = this.selectedEntity.fields.find(field =>
                field.tagEntries?.find(entry => entry.value === 'invoice_number' && field.fieldSection === sourceTable.name)
            );
            sourceFieldId = field?.uid;
            field?.uid &&
                rules.push({
                    operator: 'EXISTS',
                    value: undefined,
                    fieldId: field.uid,
                    restrictionType: 'SingleRestrictionMetadata',
                });
        }
        if (this.isGrouped) {
            const field = this.selectedEntity.fields.find(field =>
                field.tagEntries?.find(entry => entry.key === 'association' && entry.value === 'main')
            );
            groupFieldId = field?.uid;
            field &&
                rules.push({
                    operator: 'EXISTS',
                    value: undefined,
                    fieldId: field.uid,
                    restrictionType: 'SingleRestrictionMetadata',
                });
        }
        Object.entries(obj).forEach(([key, value]) => {
            let type, input: any;
            const field = this.selectedEntity.fields.find(field => field.uid === key);
            if (this.isMisMatchGroup && field.fieldSection !== sourceTable.name) {
                return;
            }
            switch (field.datatype) {
                case 'DOUBLE':
                case 'DECIMAL':
                case 'FLOAT':
                case 'INTEGER':
                    type = 'IN';
                    input = [value];
                    break;
                case 'DATE':
                    type = 'EQ';
                    input = value;
                    break;
                default:
                    type = 'CONTAINS';
                    input = `*${value}*`;
            }
            const rule = {
                operator: type,
                value: input,
                fieldId: key,
                restrictionType: 'SingleRestrictionMetadata',
            };
            if (sourceFieldId || groupFieldId) {
                const spliceIndex = rules.findIndex(
                    ruleObject => ruleObject.fieldId === sourceFieldId || ruleObject.fieldId === groupFieldId
                );
                rules.splice(spliceIndex, 1);
            }
            rules.push(rule);
        });
        return rules;
    };

    private updateAlreadySelectedRecords = (records: Record[]) => {
        const selectedRecords = this.getSelectedTableData();
        const ids = selectedRecords?.map(record => record.recordId);
        this.selectedRecordsCount = selectedRecords.length;
        if (selectedRecords.length > 0) {
            records.forEach(record => {
                if (ids.includes(record.id)) {
                    record.selected = true;
                }
            });
        }
    };

    private getSelectedTableData = () => {
        let tableData: TableData[] = [];
        Object.values(this.pageIndexVsRecords.source1).forEach(value => {
            tableData = [...tableData, ...value.tableData];
        });
        Object.values(this.pageIndexVsRecords.source2).forEach(value => {
            tableData = [...tableData, ...value.tableData];
        });
        return tableData?.filter(row => row.recordId !== undefined);
    };

    private createCriteria = (source?: 'source1' | 'source2') => {
        if (!this.isMisMatchGroup && !this.isGrouped) {
            this.store$.dispatch(
                SetFilingInstanceEntityCriteria({
                    criteria: undefined,
                    source: 'source1',
                    entityId: undefined,
                })
            );
            this.store$.dispatch(
                SetFilingInstanceEntityCriteria({
                    criteria: undefined,
                    source: 'source2',
                    entityId: undefined,
                })
            );
            return;
        }
        if (this.isGrouped) {
            const criteria = this.prepareCriteria({});
            this.store$.dispatch(
                SetFilingInstanceEntityCriteria({
                    criteria: criteria,
                    source: 'grouped',
                    entityId: undefined,
                })
            );
            return;
        }
        [
            { source: 'source1', name: this.source1 },
            { source: 'source2', name: this.source2 },
        ].forEach(sourceObj => {
            const criteria = this.prepareCriteria({}, sourceObj);
            (!source || source === sourceObj.source) &&
                this.store$.dispatch(
                    SetFilingInstanceEntityCriteria({
                        criteria: criteria,
                        source: sourceObj.source as any,
                        entityId: undefined,
                    })
                );
        });
    };

    private clearRecordsPagination = () => {
        this.store$.dispatch(
            ClearRecordsPagination({
                organizationId: this.organizationId,
                serviceId: this.serviceId,
                instanceId: this.instanceId,
                entityUid: this.selectedEntity.uid,
                parentRecordId: undefined,
            })
        );
    };

    private clearSearchCriteria = () => {
        this.selectedEntity?.uid &&
            this.store$.dispatch(
                ClearSearchCriteria({
                    organizationId: this.organizationId,
                    serviceId: this.serviceId,
                    instanceId: this.instanceId,
                    entityUid: this.selectedEntity.uid,
                    parentRecordId: this.selectedDetails?.recordId,
                })
            );
    };

    private initiateSubscriptions = () => {
        this.store$
            .select(getCurrentOrganizationId$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(orgId => {
                this.organizationId = orgId;
            });
        this.store$
            .select(getApp$(this.serviceId))
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(event => {
                this.source1 = event?.tags?.find(entry => entry?.tagKey === 'output-source-1')?.tagValue;
                this.source2 = event?.tags?.find(entry => entry?.tagKey === 'output-source-2')?.tagValue;
                this.misMatchEntityId = event?.tags?.find(entry => entry?.tagKey === 'mismatch_group')?.tagValue;
                this.movableEntityIds = event?.tags
                    ?.filter(entry => entry?.tagKey === 'movable_entities')
                    ?.reduce((ids, entry) => {
                        ids.push(entry.tagValue);
                        return ids;
                    }, []);
                this.confirmMatchEntityId = event?.tags?.find(entry => entry?.tagKey === 'confirm_match')?.tagValue;
            });
        this.store$
            .select(getFilingSelectedAppMetaData$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(event => {
                this.assetMetaData = event;
                this.entities = event?.entities;
                this.primaryEntity = this.entities?.find(entity => entity.primary);
                const entity = this.entities?.filter(entity => !entity.primary)?.[0];
                entity &&
                    this.store$.dispatch(
                        SetFilingInstanceEntityView({
                            entityId: entity.uid,
                            viewLevel: 0,
                            recordId: undefined,
                        })
                    );
            });
        this.store$
            .select(getFilingPrimaryEntityRecords$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe((records: Record[]) => {
                this.primaryEntityRecord = new Record(records?.[0], this.primaryEntity);
                this.prepareTotalCount();
            });
        this.store$
            .select(getFilingEntityRecordsCount$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isMisMatchGroup && !this.isGrouped)
            )
            .subscribe(count => {
                this.dataLength = count;
            });
        this.store$
            .select(getFilingGroupedRecordsCount$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => this.isGrouped)
            )
            .subscribe(count => {
                this.dataLength = count;
            });
        this.store$
            .select(getFilingSource1RecordsCount$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => this.isMisMatchGroup)
            )
            .subscribe(count => {
                this.source1Pagination.count = count;
            });
        this.store$
            .select(getFilingSource2RecordsCount$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => this.isMisMatchGroup)
            )
            .subscribe(count => {
                this.source2Pagination.count = count;
            });
        this.store$
            .select(getFilingNonSelectedEntity$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(entities => {
                this.nonSelectedEntities = entities?.filter(entity => !entity.primary) || [];
            });
        this.store$
            .select(getFilingSelectedEntity$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(entity => entity?.uid !== undefined)
            )
            .subscribe(entity => {
                this.isConfigurationLoaded = false;
                this.clearSearchCriteria();
                this.selectedEntity = entity;
                this.isMisMatchGroup = this.selectedEntity?.uid === this.misMatchEntityId;
                this.isGrouped =
                    !this.isMisMatchGroup &&
                    this.selectedEntity?.fields?.find(field =>
                        field.tagEntries?.find(tag => tag.key === 'association' && tag.value === 'main')
                    ) !== undefined;
                this.canShowMovableEntities = this.movableEntityIds.includes(this.selectedEntity?.uid);
                this.onEntitySelectionChange();
                this.createCriteria();
                if (this.isMisMatchGroup) {
                    this.filteredNonSelectedEntities = this.nonSelectedEntities?.filter(entity => entity.uid === this.confirmMatchEntityId);
                } else {
                    this.filteredNonSelectedEntities = this.nonSelectedEntities?.filter(entity => entity.uid === this.misMatchEntityId);
                }
                this.moveToGroup = this.filteredNonSelectedEntities?.[0]?.uid;
            });
        this.store$
            .select(getFilingEntityRecords$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined && !this.isMisMatchGroup && !this.isGrouped)
            )
            .subscribe(records => {
                const clonedRecords = CommonUtilsService.cloneObject(records);
                clonedRecords.length > 0 && this.updateAlreadySelectedRecords(clonedRecords);
                this.loadTableData(clonedRecords);
            });
        this.store$
            .select(getFilingGroupedRecords$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => this.isGrouped)
            )
            .subscribe(records => {
                const clonedRecords = CommonUtilsService.cloneObject(records);
                clonedRecords.length > 0 && this.updateAlreadySelectedRecords(clonedRecords);
                this.loadTableData(clonedRecords);
            });
        this.store$
            .select(getFilingSource1Records$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined && this.isMisMatchGroup)
            )
            .subscribe(records => {
                const clonedRecords = CommonUtilsService.cloneObject(records);
                clonedRecords.length > 0 && this.updateAlreadySelectedRecords(clonedRecords);
                this.loadTableData(clonedRecords, 'source1');
            });
        this.store$
            .select(getFilingSource2Records$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined && this.isMisMatchGroup)
            )
            .subscribe(records => {
                const clonedRecords = CommonUtilsService.cloneObject(records);
                clonedRecords.length > 0 && this.updateAlreadySelectedRecords(clonedRecords);
                this.loadTableData(clonedRecords, 'source2');
            });
        this.store$
            .select(getFilingRecordsPagination$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isGrouped && !this.isMisMatchGroup)
            )
            .subscribe(event => {
                this.source1Pagination.index = event?.pageIndex;
                this.source1Pagination.size = event?.pageSize;
            });
        this.store$
            .select(getFilingGroupedRecordsPagination$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => this.isGrouped)
            )
            .subscribe(event => {
                this.source1Pagination.index = event?.pageIndex;
                this.source1Pagination.size = event?.pageSize;
            });
        this.store$
            .select(getFilingSource1RecordsPagination$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isGrouped)
            )
            .subscribe(event => {
                this.source1Pagination.index = event?.pageIndex;
                this.source1Pagination.size = event?.pageSize;
            });
        this.store$
            .select(getFilingSource2RecordsPagination$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isGrouped)
            )
            .subscribe(event => {
                this.source2Pagination.index = event?.pageIndex;
                this.source2Pagination.size = event?.pageSize;
            });
        this.store$
            .select(getFilingSelectedEntityDetails$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(event => {
                this.currentView = event;
            });
        this.store$
            .select(getClearanceRunNumber$(this.serviceId, this.instanceId))
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(event => {
                this.clearanceRunNumber = event;
            });
        this.loadingStatus = this.store$.select(getFilingInstanceRecordsStatus$).pipe(takeUntil(this.unSubscribe));
        this.actions$.pipe(ofType(SetInstanceDataTableRecordSelect), takeUntil(this.unSubscribe), delay(10)).subscribe(() => {
            this.prepareIndexVsSelectedRecords();
            this.prepareTotalCount();
            this.selectedRecordsCount = this.getSelectedTableData().length;
        });
        this.actions$
            .pipe(
                ofType(SetRecords),
                takeUntil(this.unSubscribe),
                filter(data => data.deleteFromSource)
            )
            .subscribe(() => {
                this.deleteFromSource();
            });
        this.actions$
            .pipe(
                ofType(DeletedRecords),
                takeUntil(this.unSubscribe),
                filter(data => data.deleteFromSource)
            )
            .subscribe(() => {
                const moveToEntity = this.entities.find(entity => entity.uid === this.moveToGroup);
                this.store$.dispatch(
                    AlertSuccess({
                        message: 'Data moved to ' + moveToEntity?.displayName,
                    })
                );
                this.resetSelection();
                this.store$.dispatch(
                    GetFilingInstanceEntityRecords({ reconSpecific: this.isMisMatchGroup, source: this.isGrouped ? 'grouped' : undefined })
                );
            });
        this.store$
            .select(getFilingParentEntityDetails$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(event => {
                this.selectedDetails = event;
            });
        this.stateName = this.store$
            .select(getFilingState$(this._commonUtils.getFromStorage('gstfiling-redirect-instanceid'), this.serviceId))
            .pipe(takeUntil(this.unSubscribe));
    };

    ngOnInit(): void {
        if (this.demoMode) {
            this.prepareTableColumns();
            switch (this.isMisMatchGroup) {
                case true:
                    this.loadTableData(this.entityRecords.source1, 'source1');
                    this.loadTableData(this.entityRecords.source2, 'source2');
                    break;
                default:
                    this.loadTableData(this.entityRecords.source1);
                    break;
            }
        } else {
            this.store$.dispatch(
                SetFilingSelectedInstanceDetails({
                    instanceId: this.instanceId,
                    serviceId: this.serviceId,
                })
            );
            this.initiateSubscriptions();
        }
    }

    ngOnDestroy(): void {
        this.clearRecordsPagination();
        this.resetSelection();
        this.unSubscribe.next(undefined);
        this.unSubscribe.complete();
    }
}
